/**
 * Extracts a user-friendly error message from various error shapes.
 * @param {any} error The error object, which could be an axios error, a standard Error, or a string.
 * @returns {string} A user-friendly error message.
 */
export const getErrorMessage = (error) => {
  if (!error) {
    return 'An unexpected error occurred.';
  }

  // 1. For our backend's specific JSON error responses
  if (error.response && error.response.data && (error.response.data.message || error.response.data.error)) {
    return error.response.data.message || error.response.data.error;
  }

  // 2. For standard HTTP errors (e.g., 500, 404)
  if (error.response && error.response.statusText) {
    return error.response.statusText;
  }

  // 3. For Network Errors and standard JavaScript Error objects
  if (error.message) {
    return error.message;
  }

  // 4. For when the error is just a string
  if (typeof error === 'string') {
    return error;
  }

  // 5. Fallback for any other unknown error shapes
  return 'An unexpected error occurred.';
};

/**
 * Normalizes a blob-typed error body in place.
 *
 * Requests made with `responseType: 'blob'` (the document-download endpoints)
 * deliver an error status's body as a Blob, so the JSON message the API sends on
 * a 4xx/5xx — e.g. api-gateway PR #494's document-readiness 409 "not signed yet"
 * — is unreadable until it is converted. When `error.response.data` is a Blob
 * that parses to a JSON object, replace it with that object so callers (and
 * `getErrorMessage`) can read `error.response.data.message`.
 *
 * Best-effort and defensive: anything that is not a Blob, does not parse to a
 * JSON object, or cannot be read is left exactly as-is so the caller falls back
 * to its generic message. NEVER throws and always resolves to the same error
 * reference it was given.
 *
 * @param {any} error An axios-style error (or anything). Mutated in place.
 * @returns {Promise<any>} The same `error`.
 */
export const parseBlobErrorBody = async (error) => {
  const data = error && error.response && error.response.data;
  if (typeof Blob === 'undefined' || !(data instanceof Blob)) {
    return error;
  }

  try {
    const parsed = JSON.parse(await data.text());
    if (parsed && typeof parsed === 'object') {
      error.response.data = parsed;
    }
  } catch (e) {
    // Body was not JSON, or the Blob could not be read. Leave it untouched so
    // the caller falls back to its generic message. Deliberately swallowed —
    // this runs inside an error path and must never mask the original failure.
  }

  return error;
};
